import { useEffect, useRef } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";

import { Cursor } from "./Cursor";
import { DownloadIcon } from "./Icons";
import { AICore } from "./hero/AICore";
import { FluidCursor } from "./FluidCursor";
import { Logo } from "./Logo";
import { useApi } from "../hooks/useApi";
import { api } from "../lib/api";
import { downloadable } from "../lib/downloads";
import { ThemeToggle } from "./ThemeToggle";

/**
 * The shell every page sits in.
 *
 * Four destinations. Work, About, Contact — and the logo, which is the home
 * link. A separate "Home" text item next to a clickable wordmark is two
 * controls doing one job, and the wordmark is the one everybody already
 * reaches for.
 *
 * The page transition here comes with focus management attached, because in a
 * single-page app the two are the same problem. Clicking a link swaps the DOM
 * without moving focus or the viewport, so a keyboard user's next Tab resumes
 * from wherever they were in the old page's footer and a screen reader
 * announces nothing at all. The animation is the visible half of a fix whose
 * other half is invisible and matters more.
 */

interface NavItem {
  to: string;
  label: string;
}

/**
 * `/projects` keeps its path — "Work" is the label, and `/projects/triageai`
 * reads better in a URL bar or a shared link than `/work/triageai` does.
 */
const NAV: readonly NavItem[] = [
  { to: "/projects", label: "Work" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function navClass({ isActive }: { isActive: boolean }): string {
  return [
    "relative py-2 text-xs transition-colors duration-300",
    isActive ? "text-accent" : "text-text-dim hover:text-text",
  ].join(" ");
}

export function Layout() {
  const { pathname } = useLocation();
  const mainRef = useRef<HTMLElement>(null);
  // The *previous path*, not a "have I run yet" boolean. StrictMode invokes
  // effects twice on mount in development, which flips a boolean guard on the
  // first pass and lets the second pass run — so a plain `isFirstRender` ref
  // focuses main on every fresh page load and draws a ring around the whole
  // page. Comparing paths is idempotent, so running twice is harmless.
  const previousPath = useRef(pathname);
  // The CV path is a database field, so it can be swapped in the admin
  // without a deploy. Empty hides the button rather than linking a 404.
  const profile = useApi(() => api.profile(), []);
  const resume = profile.data?.resume_url;

  useEffect(() => {
    // Not on first paint: focusing main on load would skip the header, and
    // scrolling to top is already where the browser put us.
    if (previousPath.current === pathname) return;
    previousPath.current = pathname;

    window.scrollTo({ top: 0, behavior: "auto" });
    mainRef.current?.focus();
  }, [pathname]);

  const isHome = pathname === "/";

  return (
    <div className="flex min-h-dvh flex-col">
      <Cursor />

      {/* One canvas for the whole site, mounted here rather than per page.
          A second instance would mean a second WebGL context, a second set of
          320 nodes and a second RAF loop — and it would restart on every
          route change, so the field would visibly jump each time you
          navigated instead of carrying on underneath.

          `z-0` and not a negative z-index: the body's background paints
          first, and anything behind it is simply invisible. Content sits at
          `z-10`. Dimmer away from the hero, where it is texture rather than
          the subject. */}
      <AICore
        className={`fixed inset-0 z-0 h-full w-full transition-opacity duration-700 ${
          // Off the homepage this is texture behind body copy, and body copy has
          // to stay comfortable to read — 40% was competing with it.
          isHome ? "opacity-100" : "opacity-[0.14]"
        }`}
      />
      {/* The liquid layer sits above the network and below the page: colour
          poured by the cursor drifts over the network but never over text. A
          pointer-events-none layer over interactive content is free; the
          pointerdown it ignores is why the fluid never gets in the way. */}
      <FluidCursor />
      {/* First tab stop on every page. */}
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:rounded focus:border focus:border-accent-dim focus:bg-surface focus:px-4 focus:py-2 focus:text-text"
      >
        Skip to content
      </a>

      <header className="glass sticky top-0 z-40 border-b border-edge/60">
        <div className="mx-auto flex max-w-[1120px] items-center gap-x-8 px-6 py-4">
          <NavLink to="/" className="shrink-0">
            {/* The link is named here, once. When a real mark replaces the
                monogram its alt must stay empty or this gets announced twice. */}
            <span className="sr-only">Iftikhar Ali — home</span>
            <Logo />
          </NavLink>

          <nav aria-label="Main" className="flex flex-1 flex-wrap gap-x-7">
            {NAV.map(({ to, label }) => (
              <NavLink key={to} to={to} className={navClass}>
                {label}
              </NavLink>
            ))}
          </nav>

          {resume && (
            <a
              href={downloadable(resume)}
              download
              // The label is what folds away on a narrow screen, not the
              // button. It used to be `hidden sm:inline-flex`, so on a phone
              // there was no way to get the CV at all — on a site whose
              // audience mostly arrives on one.
              className="group inline-flex shrink-0 items-center gap-2 rounded-full border border-accent-dim px-3 py-1.5 text-2xs text-accent transition-colors duration-300 hover:bg-accent/10 sm:px-3.5"
              aria-label="Download résumé"
            >
              <DownloadIcon className="size-3.5" />
              {/* "Résumé", not "CV" — the audience for this button is mostly
                  recruiters, and it saves rather than opening a viewer tab
                  they then have to close. `download` alone cannot promise that
                  once the file is hosted off-origin; see lib/downloads.ts. */}
              <span className="hidden sm:inline">Résumé</span>
            </a>
          )}

          <ThemeToggle />
        </div>
      </header>

      <main
        id="main"
        ref={mainRef}
        tabIndex={-1}
        // No ring on this one. tabIndex -1 keeps main out of the tab order, so
        // it is never a control someone lands on by tabbing — the focus here
        // is a screen-reader and caret destination after a route change, and a
        // 2px rectangle around the entire page is not a focus indicator.
        className="relative z-10 w-full flex-1 focus:outline-none"
      >
        <div key={pathname} className="page-enter">
          <Outlet />
        </div>
      </main>

    </div>
  );
}
